import { AcademyReceiptsBoundary } from '../components/StudentPaymentReceipts';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function InvoiceList() {
  const companyId = localStorage.getItem('selectedCompanyId');
  return <AcademyReceiptsBoundary companyId={companyId}><GenericInvoiceList /></AcademyReceiptsBoundary>;
}

function GenericInvoiceList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [docStatusFilter, setDocStatusFilter] = useState('');
  const [payStatusFilter, setPayStatusFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    customer_name: '', invoice_date: new Date().toISOString().split('T')[0],
    due_date: '', currency: 'USD', document_status: 'draft',
    tax_name: '', tax_percentage: 0, discount_percentage: 0, discount_amount: 0,
    notes: '', source_module: 'invoicing',
  });
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);

  // Check if arriving with filter=unsettled
  useEffect(() => {
    if (searchParams.get('filter') === 'unsettled') {
      setDocStatusFilter('issued');
      setPayStatusFilter('unpaid');
    }
  }, []);

  const fetchInvoices = () => {
    if (!companyId) return;
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 15 });
    if (search) params.append('search', search);
    if (docStatusFilter) params.append('document_status', docStatusFilter);
    if (payStatusFilter) params.append('payment_status', payStatusFilter);
    if (currencyFilter) params.append('currency', currencyFilter);

    fetch(`${API}/companies/${companyId}/invoicing/invoices?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setInvoices(Array.isArray(d.data) ? d.data : []);
        setTotalPages(d.last_page || 1);
      })
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  };

  const fetchClients = () => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/clients?per_page=100`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setClients(Array.isArray(d.data) ? d.data : []))
      .catch(() => {});
  };

  useEffect(() => { fetchInvoices(); }, [companyId, page, search, docStatusFilter, payStatusFilter, currencyFilter]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const payload = { ...form, items };
      const res = await fetch(`${API}/companies/${companyId}/invoicing/invoices`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed');
      }
      setShowCreate(false);
      setForm({ customer_name: '', invoice_date: new Date().toISOString().split('T')[0], due_date: '', currency: 'USD', document_status: 'draft', tax_name: '', tax_percentage: 0, discount_percentage: 0, discount_amount: 0, notes: '', source_module: 'invoicing' });
      setItems([{ description: '', quantity: 1, unit_price: 0 }]);
      setPage(1);
      fetchInvoices();
    } catch (e) { alert(e.message || 'Failed to create invoice'); }
    finally { setSaving(false); }
  };

  const openCreate = () => {
    fetchClients();
    setShowCreate(true);
  };

  const addItem = () => setItems([...items, { description: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };
  const updateItem = (idx, field, val) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: val };
    setItems(updated);
  };

  const subtotal = items.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const discountAmt = form.discount_percentage > 0 ? subtotal * (form.discount_percentage / 100) : parseFloat(form.discount_amount) || 0;
  const afterDiscount = subtotal - discountAmt;
  const taxAmt = afterDiscount * ((parseFloat(form.tax_percentage) || 0) / 100);
  const total = afterDiscount + taxAmt;

  const fmt = (v) => parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const docStatusColor = { draft: '#94a3b8', issued: '#3b82f6', cancelled: '#ef4444' };
  const payStatusColor = { unpaid: '#ef4444', partially_paid: '#f59e0b', paid: '#10b981' };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>📄 Invoices</h2>
        <button onClick={openCreate} style={btnPrimary}>+ New Invoice</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input placeholder="Search invoices..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={filterInputStyle} />
        <select value={docStatusFilter} onChange={e => { setDocStatusFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Doc Status</option>
          <option value="draft">Draft</option>
          <option value="issued">Issued</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={payStatusFilter} onChange={e => { setPayStatusFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Pay Status</option>
          <option value="unpaid">Unpaid</option>
          <option value="partially_paid">Partially Paid</option>
          <option value="paid">Paid</option>
        </select>
        <select value={currencyFilter} onChange={e => { setCurrencyFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Currencies</option>
          <option value="USD">USD</option>
          <option value="LBP">LBP</option>
          <option value="EUR">EUR</option>
        </select>
      </div>

      {initialLoading ? <p>Loading...</p> : (
        <>
          <div style={{ ...cardStyle, overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Invoice #</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Due Date</th>
                  <th style={thStyle}>Total</th>
                  <th style={thStyle}>Remaining</th>
                  <th style={thStyle}>Doc Status</th>
                  <th style={thStyle}>Pay Status</th>
                  <th style={thStyle}>Currency</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No invoices found</td></tr>
                ) : invoices.map(inv => (
                  <tr key={inv.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/company/invoicing/invoices/${inv.id}`)}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: '#3b82f6' }}>{inv.invoice_number}</div>
                      {inv.is_revised && <span style={{ fontSize: '0.65rem', color: '#f59e0b' }}>✏️ Revised</span>}
                    </td>
                    <td style={tdStyle}>{inv.client?.display_name || inv.customer_name}</td>
                    <td style={tdStyle}>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—'}</td>
                    <td style={tdStyle}>
                      {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                      {inv.due_date && inv.remaining_amount > 0 && new Date(inv.due_date) < new Date() && (
                        <span style={{ ...badgeStyle, background: '#fef2f2', color: '#dc2626', marginLeft: '4px', fontSize: '0.6rem' }}>OVERDUE</span>
                      )}
                    </td>
                    <td style={tdStyle}>{fmt(inv.total)}</td>
                    <td style={{ ...tdStyle, fontWeight: inv.remaining_amount > 0 ? 600 : 400, color: inv.remaining_amount > 0 ? '#ef4444' : '#10b981' }}>{fmt(inv.remaining_amount)}</td>
                    <td style={tdStyle}>
                      <span style={{ ...badgeStyle, background: (docStatusColor[inv.document_status] || '#6b7280') + '20', color: docStatusColor[inv.document_status] || '#6b7280' }}>
                        {inv.document_status}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ ...badgeStyle, background: (payStatusColor[inv.payment_status] || '#6b7280') + '20', color: payStatusColor[inv.payment_status] || '#6b7280' }}>
                        {(inv.payment_status || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={tdStyle}>{inv.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={paginationBtn}>← Prev</button>
              <span style={{ padding: '6px 12px', fontSize: '0.85rem', color: '#64748b' }}>Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={paginationBtn}>Next →</button>
            </div>
          )}
        </>
      )}

      {/* Create Invoice Modal */}
      {showCreate && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700 }}>Create New Invoice</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Customer Name *</label>
                <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} style={modalInputStyle} placeholder="Customer name" />
                {clients.length > 0 && (
                  <select onChange={e => {
                    const c = clients.find(cl => cl.id === parseInt(e.target.value));
                    if (c) setForm({ ...form, customer_name: c.display_name || c.name, client_id: c.id });
                  }} style={{ ...modalInputStyle, marginTop: '4px' }}>
                    <option value="">— Select from clients —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.display_name || c.name}</option>)}
                  </select>
                )}
              </div>
              <div>
                <label style={labelStyle}>Invoice Date *</label>
                <input type="date" value={form.invoice_date} onChange={e => setForm({ ...form, invoice_date: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Due Date</label>
                <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Currency</label>
                <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={modalInputStyle}>
                  <option value="USD">USD</option>
                  <option value="LBP">LBP</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={form.document_status} onChange={e => setForm({ ...form, document_status: e.target.value })} style={modalInputStyle}>
                  <option value="draft">Draft</option>
                  <option value="issued">Issue Immediately</option>
                </select>
              </div>
            </div>

            {/* Line Items */}
            <h4 style={{ margin: '16px 0 8px', fontSize: '0.9rem', fontWeight: 600 }}>Line Items</h4>
            {items.map((item, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                <input placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} style={modalInputStyle} />
                <input type="number" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} style={modalInputStyle} min="0" step="any" />
                <input type="number" placeholder="Price" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} style={modalInputStyle} min="0" step="any" />
                <div style={{ fontSize: '0.85rem', color: '#334155', fontWeight: 600 }}>{fmt((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}</div>
                <button onClick={() => removeItem(idx)} style={{ ...btnCancel, padding: '4px 8px', fontSize: '0.75rem' }}>✕</button>
              </div>
            ))}
            <button onClick={addItem} style={{ ...linkBtnStyle, marginBottom: '12px' }}>+ Add Line Item</button>

            {/* Discount + Tax */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Tax Name</label>
                <input value={form.tax_name} onChange={e => setForm({ ...form, tax_name: e.target.value })} style={modalInputStyle} placeholder="e.g. VAT 11%" />
              </div>
              <div>
                <label style={labelStyle}>Tax %</label>
                <input type="number" value={form.tax_percentage} onChange={e => setForm({ ...form, tax_percentage: e.target.value })} style={modalInputStyle} min="0" step="any" />
              </div>
              <div>
                <label style={labelStyle}>Discount %</label>
                <input type="number" value={form.discount_percentage} onChange={e => setForm({ ...form, discount_percentage: e.target.value, discount_amount: 0 })} style={modalInputStyle} min="0" step="any" />
              </div>
              <div>
                <label style={labelStyle}>Discount Amt</label>
                <input type="number" value={form.discount_amount} onChange={e => setForm({ ...form, discount_amount: e.target.value, discount_percentage: 0 })} style={modalInputStyle} min="0" step="any" />
              </div>
            </div>

            {/* Totals */}
            <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px', fontSize: '0.85rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}><span>Subtotal:</span><span>{fmt(subtotal)}</span></div>
              {discountAmt > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#ef4444' }}><span>Discount:</span><span>-{fmt(discountAmt)}</span></div>}
              {taxAmt > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}><span>Tax ({form.tax_name || 'Tax'}):</span><span>{fmt(taxAmt)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '6px' }}><span>Total:</span><span>{fmt(total)}</span></div>
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...modalInputStyle, minHeight: '60px' }} placeholder="Additional notes..." />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreate(false)} style={btnCancel}>Cancel</button>
              <button onClick={handleCreate} disabled={saving || !form.customer_name || items.every(i => !i.description)} style={btnPrimary}>
                {saving ? 'Creating...' : form.document_status === 'issued' ? 'Create & Issue' : 'Create Draft'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1200px' };
const cardStyle = { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
const filterInputStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', minWidth: '180px' };
const selectStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', background: '#fff' };
const btnPrimary = { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnCancel = { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' };
const paginationBtn = { padding: '6px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' };
const modalInputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' };
const linkBtnStyle = { background: 'none', border: 'none', color: '#3b82f6', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: '4px 0' };
